import PropTypes from 'prop-types';
import {
  GridAddIcon,
  GridToolbarColumnsButton,
  GridToolbarContainer,
  GridToolbarDensitySelector,
  GridToolbarFilterButton
} from '@mui/x-data-grid';
import DeleteIcon from '@mui/icons-material/Delete';
import { Box, IconButton } from '@mui/material';
import Tooltip from '@mui/material/Tooltip';
import { useIntl } from 'react-intl';

/**
 * CustomToolbar component for the DataGrid, providing buttons for adding, deleting,
 * and duplicating rows, as well as standard toolbar functionalities like column selection,
 * filtering, and density selection.
 *
 * @component
 *
 * @example
 * <UserCustomToolbar onAdd={handleAdd} onDelete={handleDelete} onDuplicate={handleDuplicate} selectedRows={selectedRows} />
 *
 * @param {Object} props - The properties object.
 * @param {Function} props.onAdd - Function to call when the add button is clicked.
 * @param {Function} props.onDelete - Function to call when the delete button is clicked.
 * @param {Function} props.onDuplicate - Function to call when the duplicate button is clicked.
 * @param {Array} props.selectedRows - Array of selected rows.
 *
 * @returns {React.Component} The Custom Toolbar component.
 */
function UserCustomToolbar({ onAdd, onDelete, selectedRows }) {
  const { formatMessage: msg } = useIntl();

  const oneSelected = selectedRows.length === 1;
  const manySelected = selectedRows.length > 1;

  return (
    <GridToolbarContainer>
      <GridToolbarColumnsButton />
      <GridToolbarFilterButton />
      <GridToolbarDensitySelector />
      <Box sx={{ flexGrow: 1 }} />
      <Tooltip title={msg({ id: `action.create` })}>
        <IconButton
          aria-label="add"
          color="success"
          onClick={onAdd}
          type="button"
          sx={{ mr: -1 }}
          size="large"
        >
          <GridAddIcon fontSize="inherit" />
        </IconButton>
      </Tooltip>
      <Tooltip title={msg({ id: `action.delete` })}>
        <span>
          <IconButton
            type="button"
            aria-label="delete"
            color="error"
            sx={{ mr: -1 }}
            size="large"
            onClick={onDelete}
            disabled={!oneSelected && !manySelected}
          >
            <DeleteIcon />
          </IconButton>
        </span>
      </Tooltip>
    </GridToolbarContainer>
  );
}

UserCustomToolbar.propTypes = {
  onAdd: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  selectedRows: PropTypes.array.isRequired
};

export default UserCustomToolbar;
